import { useContext, useState } from 'react';
import { makeStyles } from 'tss-react/mui';
import { CardContent, Card, Typography, CardHeader, Avatar, Tooltip, Grid, Table, TableHead, TableRow, TableBody, TableCell, CircularProgress, Link as MdLink, IconButton, Zoom } from '@mui/material';
import moment from 'moment';
import { IMessage, messageSaveSchema, ticketDetailsContext } from '../../TicketDetailsContext';
import Moment from 'react-moment';
import { MessageStatus, MediaThumb, FileIcon, SimecUser, EditIconButton, MoveMessage } from 'components';
import { Form, Formik } from 'formik';
import MessageEditor from './MessageEditor';
import { IoDuplicateOutline as NewTicketIcon } from 'react-icons/io5';
import { Link } from 'react-router-dom';
import { useIntl } from 'react-intl';
import { LocalizationIds } from 'helpers/Localizations';
import { appUserContext } from 'helpers/services';


let useStyles = makeStyles()(theme => ({
    root: {
        marginTop: theme.spacing(2),
    },
    header: {
        paddingBottom: 0
    }
}));

interface IProps {
    message: IMessage
}

let Message = ({ message }: IProps) => {
    let { classes } = useStyles();
    let { formatMessage } = useIntl();

    let { isMobile } = useContext(appUserContext);
    let service = useContext(ticketDetailsContext);

    let [editNote, setEditNote] = useState<string | null>(null);


    return (
        <Card className={classes.root}>
            <CardHeader
                classes={{ root: classes.header }}
                avatar={(
                    <Avatar alt={message.username}>
                        {message.username?.charAt(0).toUpperCase()}
                    </Avatar>
                )}
                title={<SimecUser username={message.username} defaultValue={message.userFullName} tooltipPlacement="right" />}
                subheader={(
                    <>
                        <Tooltip title={`${moment(message.date).format("LLL")}`} placement="right">
                            <span>{moment(message.date).fromNow()}</span>
                        </Tooltip>

                        {message.originalTicketNumber != null && message.originalTicketNumber !== '' &&
                            <>
                                <br />
                                <Link to={`/ticket/edit/${message.originalTicketNumber}`}>
                                    {formatMessage({ id: LocalizationIds.CreatedBasedOnTicket })} {message.originalTicketNumber}
                                </Link>
                            </>
                        }

                        {message.childrenTickets != null && message.childrenTickets.length > 0 &&
                            <>
                                {message.childrenTickets.map(t =>
                                    <>
                                        <br />
                                        <Link to={`/ticket/edit/${t.value}`}>
                                            {formatMessage({ id: LocalizationIds.LinkedToTicket })} {t.value}
                                        </Link>
                                    </>
                                )}
                            </>
                        }

                    </>
                )}
                action={
                    <>
                        {isMobile === false &&
                            <MessageStatus
                                isPublic={message.shareWithCustomer}
                                source={message.source}
                            />
                        }
                    </>
                }
            />
            <CardContent>
                {isMobile === true &&
                    <Grid container>
                        <Grid item xs>
                            <MessageStatus
                                isPublic={message.shareWithCustomer}
                                source={message.source}
                            />
                        </Grid>
                    </Grid>
                }
                {editNote !== message.messageId &&
                    <Grid container>
                        <Grid item xs>
                            <Typography>
                                <span dangerouslySetInnerHTML={{ __html: message.value }}></span>
                            </Typography>
                        </Grid>
                        <Grid item>
                            {message.canCreateTicketFromMessage === true &&
                                <Tooltip title={formatMessage({ id: LocalizationIds.CreateTicketStartFrom })} TransitionComponent={Zoom}>
                                    <IconButton
                                        size="small"
                                        color="primary"
                                        component={Link}
                                        to={`/ticket/addnewfrommessage/${message.messageId}/${service.ticket.customerId}`}
                                    >
                                        <NewTicketIcon />
                                    </IconButton>
                                </Tooltip>
                            }
                            {message.canBeMoved === true &&
                                <MoveMessage messageId={message.messageId} />
                            }
                            {message.isReadOnly === false &&
                                <EditIconButton onClick={() => setEditNote(message.messageId)} />
                            }
                        </Grid>
                        {message.attachments.length > 0 &&
                            <Grid item xs={12}>
                                <Grid container>
                                    {message.attachments.filter(x => x.isImage === false && x.isVideo === false).length > 0 &&
                                        <Grid item xs={12}>
                                            <Table>
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell>File</TableCell>
                                                        <TableCell>{formatMessage({ id: LocalizationIds.LoadedFrom })}</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {message.attachments
                                                        .filter(x => x.isImage === false && x.isVideo === false)
                                                        .map((file, index) =>
                                                            <TableRow key={index}>
                                                                <TableCell>
                                                                    <MdLink
                                                                        component="button"
                                                                        disabled={service.isDownloadingDocument === file.fileId}
                                                                        onClick={(e) => {
                                                                            e.preventDefault();
                                                                            service.downloadAttachment(file.fileId);
                                                                        }}
                                                                    >
                                                                        <FileIcon extension={file.extension} />&nbsp;{file.fileName}
                                                                        {service.isDownloadingDocument === file.fileId && <CircularProgress size={20} />}
                                                                    </MdLink>
                                                                </TableCell>
                                                                <TableCell>
                                                                    <SimecUser username={file.username} />&nbsp;{formatMessage({ id: LocalizationIds.On }).toLowerCase()} <Moment date={file.date} format="LL" />
                                                                </TableCell>
                                                            </TableRow>
                                                        )}
                                                </TableBody>
                                            </Table>
                                        </Grid>
                                    }
                                    {message.attachments
                                        .filter(x => x.isImage === true || x.isVideo === true)
                                        .map(file =>
                                            <Grid item key={file.fileId}>
                                                <MediaThumb
                                                    extension={file.extension}
                                                    mediaId={file.fileId}
                                                    isImage={file.isImage}
                                                    isVideo={file.isVideo}
                                                    openInGallery={true}
                                                />
                                            </Grid>
                                        )}
                                </Grid>
                            </Grid>
                        }
                    </Grid>
                }

                {message.isReadOnly === false && editNote === message.messageId &&
                    <Grid container>
                        <Grid item xs={12}>
                            <Formik
                                validationSchema={messageSaveSchema}
                                initialValues={{
                                    ...messageSaveSchema.getDefault(),
                                    newStatus: service.ticket.status,
                                    text: message.value,
                                    messageId: message.messageId,
                                    shareWithCustomer: message.shareWithCustomer,
                                    attachments: message.attachments.map(att => ({
                                        fileId: att.fileId,
                                        fileName: att.fileName
                                    }))
                                }}
                                onSubmit={async (values: any, actions) => {
                                    await service.saveMessage(values);
                                    setEditNote(null);
                                }}
                            >
                                <Form>
                                    <MessageEditor
                                        ticketNumber={service.ticket.ticketNumber}
                                        editMessage={message}
                                        onClose={() => setEditNote(null)}
                                    />
                                </Form>
                            </Formik>
                        </Grid>
                    </Grid>
                }
            </CardContent>
        </Card>
    )
}

export default Message;